import * as React from 'react';
import classnames from 'classnames';
import { isNil } from 'lodash';

import InputCheckbox from 'components/inputs/input-checkbox/InputCheckbox';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';

import './mixed-select-dropdown-menu-item.scss';

import type { MixedSelectDropdownItem } from 'types';

import type { SyntheticEvent } from 'react';

type Props = {
  item: MixedSelectDropdownItem,
  width: number | undefined,
  inputValue?: string,
  withMulti?: boolean,
  withCheckboxes?: boolean,
  withColors?: boolean,
  withIcons?: boolean,
  withTruncate?: boolean,
  withSelectedInList?: boolean,
  withSmallSize?: boolean,
  withTags?: boolean,
  menuItemClassName?: string,
  onItemsToggle: (a: SyntheticEvent<HTMLElement>) => void
};

class MixedSelectDropdownMenuItem extends React.PureComponent<Props> {
  itemRef: React.RefObject<any> = React.createRef();

  handleItemsToggle = (event: SyntheticEvent<HTMLElement>): void => {
    const { onItemsToggle } = this.props;

    // handle the result of `onMouseDown` being used instead of `onClick`
    event.preventDefault();

    onItemsToggle(event);
  };

  renderPrimaryIcon(): React.ReactNode {
    const { item } = this.props;
    const icon = item.icon?.primary || null;

    return (
      <div className="mixed-select-dropdown-menu-item__icon mixed-select-dropdown-menu-item__icon--primary">
        <SVGDynamicIcon
          name={icon?.name ?? ''}
          width={16}
          height={16}
          customColor="black"
        />
      </div>
    );
  }

  renderSecondaryIcon(): React.ReactNode {
    const { item } = this.props;
    const icon = item.icon?.secondary || null;

    return (
      <div className="mixed-select-dropdown-menu-item__icon mixed-select-dropdown-menu-item__icon--secondary">
        <SVGDynamicIcon
          name={icon?.name ?? ''}
          width={icon?.width ?? 0}
          height={icon?.height ?? 0}
          customColor={icon?.color as any}
        />
      </div>
    );
  }

  renderSecondaryTag(): React.ReactNode {
    const { item, withTruncate } = this.props;
    const tag = item.tag?.secondary || null;

    if (isNil(tag)) {
      return null;
    }

    const tagClasses = classnames(
      'mixed-select-dropdown-menu-item__tag',
      'mixed-select-dropdown-menu-item__tag--secondary', {
        'text-overflow-ellipsis': Boolean(withTruncate)
      }
    );

    const labelClasses = classnames({
      'text-overflow-ellipsis': Boolean(withTruncate)
    });

    return (
      <div
        className={tagClasses}
        style={{ color: tag?.color, borderColor: tag?.color }}
      >
        <div
          className={labelClasses}
          {...(withTruncate ? { title: tag?.label } : null)}
        >
          {tag?.label}
        </div>
      </div>
    );
  }

  renderCheckbox(): React.ReactNode {
    const { item } = this.props;

    return (
      <InputCheckbox
        key={`mixed-select-dropdown-menu-item__checkbox-${item.identifier}`}
        id={`mixed-select-dropdown-menu-item__checkbox-${item.identifier}`}
        name="enabled"
        value={String(item.isSelected)}
        checked={item.isSelected}
      />
    );
  }

  renderColor(): React.ReactNode {
    const { item, withCheckboxes } = this.props;

    const colorClasses = classnames('mixed-select-dropdown-menu-item__color', {
      'mixed-select-dropdown-menu-item__color--with-checkboxes': Boolean(withCheckboxes)
    });

    return (
      <div
        className={colorClasses}
        style={{ backgroundColor: item.color }}
      />
    );
  }

  renderLabel(hasSecondaryTag: boolean | undefined): React.ReactNode {
    const { inputValue, item, withTruncate } = this.props;

    const labelClasses = classnames('mixed-select-dropdown-menu-item__label', {
      'mixed-select-dropdown-menu-item__label--with-truncate text-overflow-ellipsis': Boolean(withTruncate),
      'mixed-select-dropdown-menu-item__label--with-fixed-width': Boolean(hasSecondaryTag)
    });

    if (isNil(inputValue) || !inputValue) {
      return (
        <div
          className={labelClasses}
          {...(withTruncate ? { title: item.label } : null)}
        >
          {item.label}
        </div>
      );
    }

    const normalizedInput = (item.label || '').normalize();
    const normalizedSearchTerm = inputValue.normalize();

    const match = normalizedInput.toLowerCase()
      .indexOf(normalizedSearchTerm.toLowerCase());

    if (match === -1) {
      return item.label;
    }

    const head = normalizedInput.slice(0, match);
    const matchedSlice = normalizedInput.slice(match, match + normalizedSearchTerm.length);
    const tail = normalizedInput.slice(match + normalizedSearchTerm.length);

    return (
      <span>
        {head}
        <span className="mixed-select-dropdown-menu-item--highlighted">
          {matchedSlice}
        </span>
        {tail}
      </span>
    );
  }

  render(): React.ReactNode {
    const {
      item,
      width,
      withMulti,
      withCheckboxes,
      withColors,
      withIcons,
      withSelectedInList,
      withSmallSize,
      withTags,
      menuItemClassName
    } = this.props;

    const hasPrimaryIcon = withIcons && !withMulti && !withCheckboxes && !withColors && !isNil(item.icon?.primary);
    const hasSecondaryIcon = withIcons && !isNil(item.icon?.secondary);
    const hasSecondaryTag = withTags && !withMulti && !withCheckboxes && !withColors && !isNil(item.tag?.secondary);
    const isDisabled = item.isDisabled || (withSelectedInList && item.isSelected);

    const menuItemClasses = classnames('mixed-select-dropdown-menu-item', {
      'mixed-select-dropdown-menu-item--with-small-size': Boolean(withSmallSize),
      'mixed-select-dropdown-menu-item--is-disabled': isDisabled,
      [`${String(menuItemClassName)}`]: Boolean(menuItemClassName)
    });

    // reason for using `onMouseDown` instead of `onClick`
    // https://stackoverflow.com/a/28963938
    return (
      <div
        ref={this.itemRef}
        className={menuItemClasses}
        style={{ ...(hasSecondaryTag ? { width } : null) }}
        data-identifier={item.identifier}
        data-focused={item.isFocused}
        data-checked={item.isSelected}
        onMouseDown={this.handleItemsToggle}
      >
        {
          withCheckboxes && !hasPrimaryIcon &&
            this.renderCheckbox()
        }
        {
          withColors && !hasPrimaryIcon &&
            this.renderColor()
        }
        {
          hasPrimaryIcon &&
            this.renderPrimaryIcon()
        }
        {this.renderLabel(hasSecondaryTag)}
        {
          hasSecondaryIcon &&
            this.renderSecondaryIcon()
        }
        {
          hasSecondaryTag &&
            this.renderSecondaryTag()
        }
      </div>
    );
  }
}

export default MixedSelectDropdownMenuItem;
